@let groupControl = contextService.form.controls.assessableGroups.at(groupIndex());
@let $assessable = contextService.$assessable | async;

@if (isGroupVisible) {
	<mat-expansion-panel
		cdkDrag
		hideToggle
		[@zoomInOnEnter]
		[@zoomOutOnLeave]
		id="groupPanelHeader{{ groupIndex() }}"
		[class.inEditMode]="isGroupInEditMode()"
		class="groupPanel"
		[expanded]="isGroupExpanded()"
		[cdkDragDisabled]="!store.isEditPanel()"
		(afterExpand)="handleAfterExpand()"
		[attr.data-testid]="'assessable-group'"
	>
		<mat-expansion-panel-header (click)="togglePanel()">
			<mat-panel-title (keydown.space)="$event.stopImmediatePropagation()">
				@if (!isGroupInEditMode()) {
					<div class="textNonEditMode groupPanelTitle" [@zoomInOnEnter] [id]="groupHtmlId()">
						<span class="titleIndex">{{ groupIndex() + 1 }}.</span>
						{{ group().name }}
					</div>
				} @else {
					<div class="textEditMode" appStopPropagation [stopPKeyDown]="true" [@zoomInOnEnter]>
						<mat-form-field color="accent" class="full-width-form">
							<mat-label>{{ T.SHARED_EXPANSION_GROUPNAME | translate }}</mat-label>
							<input matInput [formControl]="groupControl.controls.name" [placeholder]="group().name" autofocus />
							@if (groupControl.controls.name.hasError("required")) {
								<mat-error>{{ T.ASSESSABLE_GROUP_NAME_REQUIRED | translate }}</mat-error>
							}
						</mat-form-field>
					</div>
				}

				@if (hasKoItem()) {
					<mat-icon
						class="koIcon"
						fontIcon="fa-exclamation-triangle"
						fontSet="fad"
						inline
						[matTooltip]="T.ASSESSABLE_GROUP_KO_ITEM_HINT | translate"
						matTooltipClass="warning"
					/>
				}
			</mat-panel-title>

			<mat-panel-description>
				<div class="orderActions">
					<ng-container *ngTemplateOutlet="scoreTemplate(); context: {$implicit: {groupId: group().groupId}}" />

					@if (store.isEditPanel()) {
						<button
							mat-button
							color="accent"
							class="editButton"
							appStopPropagation
							[disabled]="isGroupInEditMode() ? isSaveButtonDisabled() : isEditButtonDisabled()"
							(click)="store.toggleGroupEditMode(group().groupId)"
						>
							<sim-animated-material-icon
								class="editButtonIcon"
								[endIcon]="{
									fontSet: 'fad',
									fontIcon: 'fa-check',
								}"
								[startIcon]="{
									fontSet: 'fad',
									fontIcon: 'fa-edit',
									themeColor: 'accent',
								}"
								[trigger]="isGroupInEditMode()"
							/>

							{{ (isGroupInEditMode() ? T.ASSESSMENT_SAVE_BUTTON : T.ASSESSMENT_EDIT_BUTTON) | translate }}
						</button>
					}

					@if (store.isEditPanel()) {
						<button
							[class.dragHandle]="contextService.form.valid"
							appStopPropagation
							[disabled]="!contextService.form.valid"
							[disableRipple]="true"
							cdkDragHandle
							mat-icon-button
							color="accent"
							[matTooltip]="T.ASSESSABLE_GROUPS_DRAG_HINT | translate"
							[attr.aria-label]="T.ASSESSABLE_GROUPS_DRAG_HINT | translate"
							matTooltipClass="accent"
						>
							<mat-icon fontSet="fa" fontIcon="fa-arrows" inline />
						</button>
					}

					@if (store.isEditPanel()) {
						<mat-divider [vertical]="true" />
					}

					@if ((store.isEditPanel() || store.mode() === AssessablePanelMode.View) && group().activationCondition !== null) {
						<mat-icon
							class="activationConditionIcon"
							fontIcon="fa-link"
							fontSet="fad"
							inline
							color="accent"
							matTooltipClass="accent"
							[matTooltip]="T.ASSESSABLE_GROUPS_ACTIVATION_CONDITION_HINT | translate"
						/>
					}

					<!-- Group progress indicator -->
					@if (!store.isEditPanel()) {
						<div class="groupProgress" [class.completed]="isGroupAnswered()">
							<mat-icon inline class="icon" fontSet="fad" fontIcon="fa-list-ol" />
							<div>
								<span>{{ groupItemsAnswered() }}</span
								>/<span>{{ groupItems() }}</span>
							</div>
						</div>
					}

					<button
						mat-icon-button
						color="accent"
						matTooltipClass="accent"
						[matTooltip]="(isGroupExpanded() ? T.SHARED_EXPANSION_TOGGLE_CLOSE : T.SHARED_EXPANSION_TOGGLE_OPEN) | translate"
						[attr.aria-label]="
							(isGroupExpanded() ? T.SHARED_EXPANSION_TOGGLE_CLOSE : T.SHARED_EXPANSION_TOGGLE_OPEN) | translate
						"
						appStopPropagation
						(click)="togglePanel()"
					>
						<sim-animated-material-icon
							[endIcon]="{
								fontIcon: 'fa-chevron-up',
								fontSet: 'fad',
							}"
							[startIcon]="{
								fontSet: 'fad',
								fontIcon: 'fa-chevron-down',
								themeColor: 'accent',
							}"
							[trigger]="isGroupExpanded()"
						/>
					</button>

					@if (store.isEditPanel()) {
						<button
							mat-icon-button
							color="accent"
							appStopPropagation
							[matMenuTriggerFor]="moreOptionsChecklistPanelGroup"
							[attr.aria-label]="T.GLOBAL_TXT_MORE_OPTIONS | translate"
						>
							<mat-icon fontSet="fad" fontIcon="fa-ellipsis-v-alt" />
						</button>
					}

					<mat-menu #moreOptionsChecklistPanelGroup="matMenu">
						@if (GeneratedTypePredicates.isAssessment($assessable)) {
							<button
								mat-menu-item
								class="menueButtonWithFAIcon accent"
								(click)="contextService.changeActivationCondition(group())"
								[disabled]="!contextService.form.valid"
								[matTooltip]="T.ASSESSABLE_GROUP_CHANGE_ACTIVATION_CONDITION | translate"
								matTooltipClass="accent"
							>
								<mat-icon class="smaller" fontSet="fad" fontIcon="fa-link" />
								<span>{{ T.ASSESSABLE_GROUP_CHANGE_ACTIVATION_CONDITION | translate }}</span>
							</button>
						}

						<button
							mat-menu-item
							class="menueButtonWithFAIcon accent"
							(click)="contextService.shiftGroup('up', groupIndex())"
							[matTooltip]="T.ASSESSABLE_GROUP_MOVE_UP | translate"
							matTooltipClass="accent"
							[disabled]="groupIndex() === 0 || !contextService.form.valid"
						>
							<mat-icon class="smaller" fontSet="fad" fontIcon="fa-arrow-up" />
							<span>{{ T.ASSESSABLE_GROUP_MOVE_UP | translate }}</span>
						</button>

						<button
							mat-menu-item
							class="menueButtonWithFAIcon accent"
							(click)="contextService.shiftGroup('down', groupIndex())"
							[matTooltip]="T.ASSESSABLE_GROUP_MOVE_DOWN | translate"
							matTooltipClass="accent"
							[disabled]="groupIndex() === $assessable.assessableGroups?.length - 1 || !contextService.form.valid"
						>
							<mat-icon class="smaller" fontSet="fad" fontIcon="fa-arrow-down" />
							<span>{{ T.ASSESSABLE_GROUP_MOVE_DOWN | translate }}</span>
						</button>

						<button
							mat-menu-item
							class="menueButtonWithFAIcon warn"
							(click)="proxyService.removeGroup(group().groupId, groupIndex())"
						>
							<mat-icon class="smaller" fontSet="fad" fontIcon="fa-trash-alt" />
							<span>{{ T.ASSESSABLE_GROUP_REMOVE | translate }}</span>
						</button>
					</mat-menu>
				</div>
			</mat-panel-description>
		</mat-expansion-panel-header>

		@if (group().items && contextService.form && isGroupExpanded()) {
			<mat-accordion
				[class.checklistEditable]="store.isEditPanel()"
				cdkDropList
				[id]="group().groupId"
				[cdkDropListData]="groupControl.controls.items"
				(cdkDropListDropped)="contextService.dropItemInsideGroup($event)"
			>
				@for (item of group().items; track item.assessableItemId; let itemIndex = $index) {
					<div
						cdkDrag
						class="assessableItemPanel"
						[cdkDragDisabled]="!store.isEditPanel() || !contextService.form.valid"
						[cdkDragPreviewContainer]="previewElement()"
					>
						<ng-template #dragHandle>
							@if (store.isEditPanel()) {
								<button
									[class.dragHandle]="contextService.form.valid"
									appStopPropagation
									[disableRipple]="true"
									[disabled]="!contextService.form.valid"
									cdkDragHandle
									mat-icon-button
									color="accent"
									[matTooltip]="T.ASSESSABLE_ITEM_DRAG_HINT | translate"
									matTooltipClass="accent"
									[attr.aria-label]="T.ASSESSABLE_ITEM_DRAG_HINT | translate"
								>
									<mat-icon fontSet="fa" fontIcon="fa-arrows" inline />
								</button>
							}
						</ng-template>
						<ng-template
							[ngTemplateOutlet]="itemTemplateRef()"
							[ngTemplateOutletContext]="{
								$implicit: {
									dragHandle: dragHandle,
									item: item,
									groupItems: group().items,
									groupId: group().groupId,
									index: itemIndex,
									groupIndex: groupIndex(),
									length: group().items.length,
								},
							}"
						/>
					</div>
				}

				@if (group().items.length === 0) {
					<div class="emptyGroupContainer" [@zoomInOnEnter]>{{ T.ASSESSABLE_GROUP_PANEL_CREATE_ITEM_HINT | translate }}</div>
				}

				@if (store.isEditPanel()) {
					<div class="addItemToGroupBtn">
						<button
							mat-raised-button
							color="accent"
							[disabled]="!contextService.form.valid || store.groupInEditMode()"
							(click)="proxyService.addItemToGroup(groupIndex())"
						>
							{{ addItemToGroupLocalizationKey() | translate }}
						</button>
					</div>
				}
			</mat-accordion>
		}

		<mat-action-row
			class="bottomContent"
			[ngClass]="{
				hidden: store.isAnswerPanel() && !group().note,
			}"
		>
			@if (isGroupExpanded()) {
				<sim-note-area
					class="groupNote"
					[formControl]="groupControl.controls.note"
					[mode]="store.mode()"
					[inEditMode]="isGroupInEditMode()"
					[canEnterEditMode]="contextService.form.valid"
					(onNoteButtonClick)="store.toggleGroupEditMode(group().groupId)"
				/>
			}

			@if (store.isEditPanel() && groupIndex() > 0) {
				<button mat-button color="warn" (click)="expandPreviousGroup()">
					{{ T.SHARED_EXPANSION_PREVIOUS_GROUP | translate }}
				</button>
			}
			@if (store.isEditPanel() && groupIndex() < $assessable.assessableGroups.length - 1) {
				<button mat-button color="accent" (click)="expandNextGroup()">
					{{ T.SHARED_EXPANSION_NEXT_GROUP | translate }}
				</button>
			}
		</mat-action-row>
	</mat-expansion-panel>
}
